import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import {
  type SupportedLanguage,
  type TranslationSchema,
  type LanguageMeta,
  LANGUAGES,
  dictionaries,
} from "../locales";
import {
  resolveRegionProfile,
  localizeMetricsInString,
  localizeSplitCurrencyNodes,
} from "./regionMatrix";

const STORAGE_KEY = "credominion_lang";
const COUNTRY_STORAGE_KEY = "credominion_country";
const VALID_LANGS = new Set<SupportedLanguage>(["en", "sv", "no", "fi", "da"]);
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA"]);
const TRANSLATABLE_ATTRS = ["placeholder", "title", "aria-label", "alt"];
const METRIC_PATTERN = /\$(?=[0-9])|\b(?:USD|SqFt|sq\.?\s*ft|sqft|miles|acres)\b/i;

let currentGlobalLanguage: SupportedLanguage = "en";
let currentGlobalCountry = "";

function getActiveRegionProfile(lang: SupportedLanguage = currentGlobalLanguage, country: string = currentGlobalCountry) {
  const tz = typeof window !== "undefined" ? (Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone || "") : "";
  return resolveRegionProfile(lang, country, tz);
}

function getPathValue(obj: any, pathStr: string): string | undefined {
  if (!obj || !pathStr) return undefined;
  const parts = pathStr.split(".");
  let curr = obj;
  for (const part of parts) {
    if (curr && typeof curr === "object" && part in curr) {
      curr = curr[part];
    } else {
      return undefined;
    }
  }
  return typeof curr === "string" ? curr : undefined;
}

export function t(pathStr: string, fallback?: string): string {
  const activeDict = dictionaries[currentGlobalLanguage] || dictionaries.en;
  const localized = getPathValue(activeDict, pathStr);
  const base =
    localized !== undefined
      ? localized
      : getPathValue(dictionaries.en, pathStr) ?? (fallback || pathStr);
  return localizeMetricsInString(base, getActiveRegionProfile());
}

function detectFromClientFallback(): SupportedLanguage {
  if (typeof window === "undefined") return "en";
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.includes("Stockholm")) return "sv";
    if (tz.includes("Oslo")) return "no";
    if (tz.includes("Helsinki")) return "fi";
    if (tz.includes("Copenhagen")) return "da";

    const navLangs = navigator.languages || [navigator.language || ""];
    for (const l of navLangs) {
      const lower = String(l).toLowerCase();
      if (lower.startsWith("sv")) return "sv";
      if (lower.startsWith("no") || lower.startsWith("nb") || lower.startsWith("nn")) return "no";
      if (lower.startsWith("fi")) return "fi";
      if (lower.startsWith("da")) return "da";
    }
  } catch (e) {}
  return "en";
}

function buildUniversalReverseMap(allDicts: any): Map<string, string> {
  const map = new Map<string, string>();
  function walk(obj: any, prefix = "") {
    if (!obj || typeof obj !== "object") return;
    for (const [k, v] of Object.entries(obj)) {
      const p = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === "object") {
        walk(v, p);
      } else if (typeof v === "string" && v.trim()) {
        const norm = v.replace(/\s+/g, " ").trim();
        if (!map.has(norm)) map.set(norm, p);
      }
    }
  }
  if (allDicts?.en) walk(allDicts.en);
  for (const lang of ["sv", "no", "fi", "da"]) {
    if (allDicts?.[lang]) walk(allDicts[lang]);
  }
  return map;
}

let cachedReverseMap: Map<string, string> | null = null;
function getReverseMap(): Map<string, string> {
  if (!cachedReverseMap && dictionaries) {
    cachedReverseMap = buildUniversalReverseMap(dictionaries);
  }
  return cachedReverseMap || new Map();
}

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: typeof t;
  dict: TranslationSchema;
  currentMeta: LanguageMeta;
  isDashboard: boolean;
  supportedLanguages: LanguageMeta[];
  isEurope: boolean;
}

const EUROPEAN_COUNTRIES = new Set([
  "AL","AD","AT","AX","BA","BE","BG","BY","CH","CY","CZ","DE","DK","EE","ES",
  "FI","FO","FR","GB","GG","GI","GR","HR","HU","IE","IM","IS","IT","JE","LI",
  "LT","LU","LV","MC","MD","ME","MK","MT","NL","NO","PL","PT","RO","RS","RU",
  "SE","SI","SJ","SK","SM","UA","VA","XK"
]);

export function checkIsEurope(countryCode?: string): boolean {
  if (countryCode) return EUROPEAN_COUNTRIES.has(countryCode.toUpperCase());
  if (typeof window !== "undefined") {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      if (tz.startsWith("Europe/") || tz === "Atlantic/Reykjavik") return true;
    } catch (e) {}
  }
  return false;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>(() => {
    if (typeof window !== "undefined") {
      try {
        const savedCountry = localStorage.getItem(COUNTRY_STORAGE_KEY);
        if (savedCountry) currentGlobalCountry = savedCountry;
        const saved = localStorage.getItem(STORAGE_KEY) as SupportedLanguage | null;
        if (saved && VALID_LANGS.has(saved)) {
          currentGlobalLanguage = saved;
          return saved;
        }
      } catch (e) {}
    }
    return "en";
  });
  const [detectedCountry, setDetectedCountry] = useState<string>(() => currentGlobalCountry);

  currentGlobalLanguage = selectedLanguage;
  currentGlobalCountry = detectedCountry;

  useEffect(() => {
    if (typeof window === "undefined") return;
    let hasSavedLang = false;
    try {
      const saved = localStorage.getItem(STORAGE_KEY) as SupportedLanguage | null;
      if (saved && VALID_LANGS.has(saved)) hasSavedLang = true;
    } catch (e) {}

    let isMounted = true;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 1200);

    fetch("/cdn-cgi/trace", { signal: controller.signal })
      .then((res) => (res.ok ? res.text() : Promise.reject()))
      .then((txt) => {
        const m = txt.match(/^loc=([A-Z]{2})/m);
        return { country: m ? m[1] : "" };
      })
      .catch(() => fetch("/api/geo", { signal: controller.signal }).then((r) => (r.ok ? r.json() : Promise.reject())))
      .then((data) => {
        clearTimeout(timeoutId);
        if (!isMounted) return;
        const country = String(data?.country || "").toUpperCase();
        if (country) {
          currentGlobalCountry = country;
          setDetectedCountry(country);
          try { localStorage.setItem(COUNTRY_STORAGE_KEY, country); } catch (e) {}
        }
        if (!hasSavedLang) {
          let detected: SupportedLanguage = "en";
          if (country === "SE") detected = "sv";
          else if (country === "NO") detected = "no";
          else if (country === "FI") detected = "fi";
          else if (country === "DK") detected = "da";
          else detected = detectFromClientFallback();
          currentGlobalLanguage = detected;
          setSelectedLanguage(detected);
        }
      })
      .catch(() => {
        clearTimeout(timeoutId);
        if (isMounted && !hasSavedLang) {
          const fallback = detectFromClientFallback();
          currentGlobalLanguage = fallback;
          setSelectedLanguage(fallback);
        }
      });

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  const handleSetLanguage = useCallback((lang: SupportedLanguage) => {
    currentGlobalLanguage = lang;
    setSelectedLanguage(lang);
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(STORAGE_KEY, lang);
      } catch (e) {}
    }
  }, []);

  const regionProfile = useMemo(
    () => getActiveRegionProfile(selectedLanguage, detectedCountry),
    [selectedLanguage, detectedCountry]
  );

  // Layer 2: Zero-Remount Live DOM & Regional Currency/Unit Synchronizer
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = selectedLanguage;

    const revMap = getReverseMap();
    const activeDict = dictionaries[selectedLanguage] || dictionaries.en;
    const enDict = dictionaries.en;

    function translateDomTree() {
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);
      let node: any;
      while ((node = walker.nextNode())) {
        const parentEl = node.parentElement;
        if (!parentEl) continue;
        if (SKIP_TAGS.has(parentEl.tagName)) continue;
        if (parentEl.closest && parentEl.closest('[role="listbox"]')) continue;

        localizeSplitCurrencyNodes(node, regionProfile);

        const raw = node.nodeValue || "";
        const clean = raw.replace(/\s+/g, " ").trim();
        if (!clean) continue;

        if (!node.__i18nPath || (node.__i18nLastVal && raw !== node.__i18nLastVal)) {
          const matchedPath = revMap.get(clean);
          if (matchedPath) {
            node.__i18nPath = matchedPath;
            node.__i18nLead = raw.match(/^\s*/)?.[0] ?? "";
            node.__i18nTrail = raw.match(/\s*$/)?.[0] ?? "";
          }
        }

        const pathKey = node.__i18nPath;
        if (pathKey) {
          const targetDict = selectedLanguage === "en" ? enDict : activeDict;
          const baseVal = getPathValue(targetDict, pathKey) || getPathValue(enDict, pathKey);
          if (baseVal) {
            const targetVal = localizeMetricsInString(baseVal, regionProfile);
            const nextRaw = (node.__i18nLead || "") + targetVal + (node.__i18nTrail || "");
            if (node.nodeValue !== nextRaw) {
              node.nodeValue = nextRaw;
              node.__i18nLastVal = nextRaw;
            }
          }
        } else if (
          (!node.__i18nOrigMetricRaw && METRIC_PATTERN.test(raw)) ||
          (node.__i18nOrigMetricRaw && node.__i18nLastMetricVal && raw !== node.__i18nLastMetricVal)
        ) {
          node.__i18nOrigMetricRaw = raw;
        }

        if (!pathKey && node.__i18nOrigMetricRaw) {
          const nextRaw = localizeMetricsInString(node.__i18nOrigMetricRaw, regionProfile);
          if (node.nodeValue !== nextRaw) {
            node.nodeValue = nextRaw;
            node.__i18nLastMetricVal = nextRaw;
          }
        }
      }

      const attrEls = document.querySelectorAll("[placeholder],[title],[aria-label],[alt]");
      for (let i = 0; i < attrEls.length; i++) {
        const el: any = attrEls[i];
        if (el.closest && el.closest('[role="listbox"]')) continue;
        if (!el.__i18nAttrs) el.__i18nAttrs = {};
        for (const attr of TRANSLATABLE_ATTRS) {
          if (!el.hasAttribute(attr)) continue;
          const val = (el.getAttribute(attr) || "").replace(/\s+/g, " ").trim();
          if (!val) continue;
          if (!el.__i18nAttrs[attr]) {
            const matchedPath = revMap.get(val);
            if (matchedPath) el.__i18nAttrs[attr] = matchedPath;
          }
          const pathKey = el.__i18nAttrs[attr];
          if (pathKey) {
            const targetDict = selectedLanguage === "en" ? enDict : activeDict;
            const baseVal = getPathValue(targetDict, pathKey) || getPathValue(enDict, pathKey);
            if (baseVal) {
              const targetVal = localizeMetricsInString(baseVal, regionProfile);
              if (el.getAttribute(attr) !== targetVal) {
                el.setAttribute(attr, targetVal);
              }
            }
          }
        }
      }
    }

    translateDomTree();
    const timer = setTimeout(translateDomTree, 150);
    let rafId: number | null = null;
    const observer = new MutationObserver((mutations) => {
      const onlySwitcher = mutations.every(
        (m) => m.target && (m.target as Element).closest && (m.target as Element).closest('[role="listbox"]')
      );
      if (onlySwitcher) return;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(translateDomTree);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(timer);
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [selectedLanguage, regionProfile]);

  const currentMeta = useMemo(
    () => LANGUAGES[selectedLanguage] || LANGUAGES.en,
    [selectedLanguage]
  );
  const supportedLanguages = useMemo(() => Object.values(LANGUAGES), []);

  const value = useMemo(
    () => ({
      language: selectedLanguage,
      setLanguage: handleSetLanguage,
      t,
      dict: dictionaries[selectedLanguage] || dictionaries.en,
      currentMeta,
      isDashboard: false,
      supportedLanguages,
      isEurope: checkIsEurope(detectedCountry),
    }),
    [selectedLanguage, handleSetLanguage, currentMeta, supportedLanguages, detectedCountry]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (!context) {
    return {
      language: currentGlobalLanguage,
      setLanguage: () => {},
      t,
      dict: dictionaries[currentGlobalLanguage] || dictionaries.en,
      currentMeta: LANGUAGES[currentGlobalLanguage] || LANGUAGES.en,
      isDashboard: false,
      supportedLanguages: Object.values(LANGUAGES),
      isEurope: checkIsEurope(currentGlobalCountry),
    };
  }
  return context;
}

export { LANGUAGES };
